import { getJson, getList, sendJson, type PaginationMeta } from '@/lib/api';
import type { StatsInterval } from '@/lib/leads';
import { PAGE_TRAFFIC, type PageTraffic } from '@/lib/page-types';

/**
 * Page analytics (GA4 + Search Console mirror) — typed fetchers over
 * /api/v3/pages. Shapes mirror apps/api's PageListRow / PageSeriesStats /
 * PageSyncStatus.
 */

/** Ways to split the pages chart into series. */
export const PAGE_SEGMENT = {
  template: 'template',
} as const;
export type PageSegment = (typeof PAGE_SEGMENT)[keyof typeof PAGE_SEGMENT];

/** Sortable columns on GET /pages (page_stats columns plus the lead-join columns). */
export const PAGE_SORT = {
  clicks: 'clicks',
  impressions: 'impressions',
  ctr: 'ctr',
  position: 'position',
  sessions: 'sessions',
  path: 'path',
  leads: 'leads',
  assisted: 'assisted',
  leadRate: 'leadRate',
  assistedRate: 'assistedRate',
  sessionsChange: 'sessionsChange',
  impressionsChange: 'impressionsChange',
  positionChange: 'positionChange',
  firstSeen: 'firstSeen',
  lastSeen: 'lastSeen',
} as const;
export const PAGE_SORT_KEYS = Object.values(PAGE_SORT);

/** One row of the pages table: per-page sums over the window. */
export interface PageListRow {
  readonly path: string;
  readonly title: string;
  readonly template: string;
  readonly clicks: number;
  readonly impressions: number;
  /** Σclicks / Σimpressions (a 0–1 fraction), null when no impressions. */
  readonly ctr: number | null;
  readonly avgPosition: number | null;
  readonly sessions: number;
  /** Leads that submitted the form on this page in the window (Convert_Page). */
  readonly leads: number;
  /** Leads whose first-touch landing page was this page in the window. */
  readonly assisted: number;
  /** leads / sessions (a 0–1 fraction), null when the page had no sessions. */
  readonly leadRate: number | null;
  /** assisted / sessions (a 0–1 fraction), null when the page had no sessions. */
  readonly assistedRate: number | null;
  /** The same-length window before `from`; null when the page had no rows there. */
  readonly previous: {
    readonly clicks: number;
    readonly impressions: number;
    readonly avgPosition: number | null;
    readonly sessions: number;
  } | null;
  /** Sessions vs the previous window, a signed percent; null without a baseline. */
  readonly sessionsChangePct: number | null;
  readonly impressionsChangePct: number | null;
  /** Positions gained since the previous window (positive = moved up); null without both. */
  readonly positionChange: number | null;
  /** The first day Google Analytics or Search Console recorded the page (YYYY-MM-DD). */
  readonly firstSeen: string | null;
  /** True when that day is where our history starts, so the page is at least that old. */
  readonly firstSeenAtHistoryStart: boolean;
  /** The most recent day Google Analytics or Search Console recorded the page (YYYY-MM-DD). */
  readonly lastSeen: string | null;
  /** Whether this page is on the analytics hide list (only present with includeHidden). */
  readonly excluded?: boolean;
}

export interface PageSeriesPoint {
  readonly period: string;
  readonly clicks: number;
  readonly impressions: number;
  readonly ctr: number | null;
  readonly avgPosition: number | null;
  readonly sessions: number;
}

/** One chart series when split by template — every metric, per bucket. */
export interface PageSegmentSeries {
  readonly key: string;
  readonly clicks: readonly number[];
  readonly impressions: readonly number[];
  readonly ctr: readonly (number | null)[];
  readonly avgPosition: readonly (number | null)[];
  readonly sessions: readonly number[];
  readonly total: number;
}

export interface PageSeriesStats {
  readonly range: {
    readonly from: string;
    readonly to: string;
    readonly interval: StatsInterval;
    readonly leadIn: number;
  };
  readonly totals: {
    readonly clicks: number;
    readonly impressions: number;
    readonly ctr: number | null;
    readonly avgPosition: number | null;
    readonly sessions: number;
    readonly previous: {
      readonly clicks: number;
      readonly impressions: number;
      readonly ctr: number | null;
      readonly avgPosition: number | null;
      readonly sessions: number;
    };
    /** clicks vs previous.clicks, as a signed percent (null when no baseline). */
    readonly changePct: number | null;
  };
  readonly series: readonly PageSeriesPoint[];
  readonly segments: readonly PageSegmentSeries[] | null;
  /** Aligned to `series`: the same buckets one year earlier, every metric. */
  readonly yoy: readonly Omit<PageSeriesPoint, 'period'>[] | null;
}

export interface PageSyncStatus {
  readonly pages: number;
  readonly statRows: number;
  /** Trails ~1–2 days behind today — GSC reporting lag, not a fault. */
  readonly latestStatDate: string | null;
  readonly lastRun: {
    readonly id: string;
    readonly finishedAt: string | null;
    readonly error: string | null;
  } | null;
}

/** One page's directory row, as GET /pages/detail returns it. */
export interface PageDetail {
  readonly path: string;
  readonly title: string;
  readonly template: string;
  readonly firstSeenAt: string;
  readonly lastSeenAt: string;
}

/** One breakdown row from GET /pages/sources (channel or source). */
export interface SourceBreakdownRow {
  readonly key: string;
  readonly sessions: number;
  readonly previousSessions: number;
  readonly conversions: number;
}

/** What the sources trend series splits by. */
export type SourcesSeriesBy = 'channel' | 'source';

/** GA4 traffic sources for one page or site-wide, proxied live from Google. */
export interface PageSourcesStats {
  readonly range: { readonly from: string; readonly to: string; readonly interval: StatsInterval };
  readonly channels: readonly SourceBreakdownRow[];
  readonly sources: readonly SourceBreakdownRow[];
  readonly series: {
    readonly by: SourcesSeriesBy;
    readonly periods: readonly string[];
    readonly perKey: ReadonlyArray<{ readonly key: string; readonly values: readonly number[] }>;
  };
}

/** Which mirror metric a movers report ranks by. */
export const MOVER_METRICS = ['clicks', 'sessions', 'impressions'] as const;
export type MoverMetric = (typeof MOVER_METRICS)[number];

/** One row of GET /pages/movers. */
export interface MoverRow {
  readonly path: string;
  readonly title: string;
  readonly template: string;
  readonly current: number;
  readonly previous: number;
  readonly delta: number;
  readonly deltaPct: number | null;
}

export interface PageMoversPayload {
  readonly range: {
    readonly from: string;
    readonly to: string;
    readonly compareFrom: string;
    readonly compareTo: string;
    readonly metric: MoverMetric;
  };
  readonly drops: readonly MoverRow[];
  readonly gains: readonly MoverRow[];
}

/** One GSC query row from GET /pages/queries. */
export interface PageQueryRow {
  readonly query: string;
  readonly clicks: number;
  readonly impressions: number;
  readonly ctr: number;
  readonly position: number;
  readonly previous: { readonly clicks: number; readonly impressions: number; readonly position: number } | null;
}

export interface PageQueriesStats {
  readonly range: { readonly from: string; readonly to: string };
  readonly queries: readonly PageQueryRow[];
}

/** One day of a single query on a page, from GET /pages/queries/detail. */
export interface QueryDayRow {
  readonly date: string;
  readonly clicks: number;
  readonly impressions: number;
  readonly ctr: number;
  readonly position: number;
}

/** One of our pages that ranks for a query. */
export interface QueryPageRow {
  readonly page: string;
  readonly clicks: number;
  readonly impressions: number;
  readonly position: number;
}

export interface QueryDetailStats {
  readonly range: { readonly from: string; readonly to: string };
  readonly query: string;
  /** Day by day, oldest first; days without impressions are absent. */
  readonly series: readonly QueryDayRow[];
  /** Every page of ours Google showed for the query, most clicked first. */
  readonly pages: readonly QueryPageRow[];
}

/** Where a page lives on the marketing site. */
export function publicPageUrl(path: string): string {
  return `https://bestcorporateevents.com${path === '/' ? '' : path}`;
}

export function listPages(
  params: {
    search?: string;
    minImpressions?: number;
    /** Only pages of this template (page type). */
    template?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
    sort?: string;
    dir?: 'asc' | 'desc';
    includeHidden?: boolean;
    /** Paid: sessions and leads from Google Ads only (see PAGE_TRAFFIC). */
    traffic?: PageTraffic;
  },
  signal?: AbortSignal,
): Promise<{ items: PageListRow[]; pagination: PaginationMeta }> {
  const { includeHidden, traffic, ...rest } = params;
  return getList<PageListRow>(
    '/pages',
    { ...rest, ...(includeHidden ? { includeHidden: '1' } : {}), ...(traffic === PAGE_TRAFFIC.paid ? { traffic } : {}) },
    signal,
  );
}

export function readPageSeries(
  params: {
    from: string;
    to: string;
    interval: StatsInterval;
    leadIn?: number;
    segment?: PageSegment;
    path?: string;
    yoy?: '1';
    includeHidden?: boolean;
  },
  signal?: AbortSignal,
): Promise<PageSeriesStats> {
  const { includeHidden, ...rest } = params;
  return getJson<PageSeriesStats>('/pages/series', { ...rest, ...(includeHidden ? { includeHidden: '1' } : {}) }, signal);
}

export function readPageDetail(path: string, signal?: AbortSignal): Promise<PageDetail> {
  return getJson<PageDetail>('/pages/detail', { path }, signal);
}

export function readPageMovers(
  params: { from: string; to: string; shiftDays: number; metric: MoverMetric; includeHidden?: boolean },
  signal?: AbortSignal,
): Promise<PageMoversPayload> {
  const { includeHidden, ...rest } = params;
  return getJson<PageMoversPayload>('/pages/movers', { ...rest, ...(includeHidden ? { includeHidden: '1' } : {}) }, signal);
}

export function readPageSources(
  params: { from: string; to: string; interval: StatsInterval; path?: string; seriesBy?: SourcesSeriesBy; includeHidden?: boolean },
  signal?: AbortSignal,
): Promise<PageSourcesStats> {
  const { includeHidden, ...rest } = params;
  return getJson<PageSourcesStats>('/pages/sources', { ...rest, ...(includeHidden ? { includeHidden: '1' } : {}) }, signal);
}

export function readPageQueries(
  params: { from: string; to: string; path: string },
  signal?: AbortSignal,
): Promise<PageQueriesStats> {
  return getJson<PageQueriesStats>('/pages/queries', params, signal);
}

/** One bucket of a page's lead conversions over time. */
export interface PageLeadsPoint {
  readonly day: string;
  readonly onPage: number;
  readonly assisted: number;
}

/** A page's lead conversions over the window: on-page (converted here) and assisted (landed here). */
export interface PageLeadsStats {
  readonly path: string;
  readonly range: { readonly from: string; readonly to: string; readonly interval: StatsInterval };
  readonly totals: { readonly onPage: number; readonly assisted: number };
  readonly series: readonly PageLeadsPoint[];
}

export function readPageLeads(
  params: { from: string; to: string; path: string; interval?: StatsInterval },
  signal?: AbortSignal,
): Promise<PageLeadsStats> {
  return getJson<PageLeadsStats>('/pages/leads', params, signal);
}

/** One query's trend on a page, or across the site when `path` is absent, plus our pages ranking for it. */
export function readPageQueryDetail(
  params: { from: string; to: string; path?: string; query: string },
  signal?: AbortSignal,
): Promise<QueryDetailStats> {
  return getJson<QueryDetailStats>('/pages/queries/detail', params, signal);
}

/** Every search query Google showed any of our pages for, with previous-window numbers. */
export function readPageKeywords(
  params: { from: string; to: string; search?: string; includeHidden?: boolean },
  signal?: AbortSignal,
): Promise<PageQueriesStats> {
  const { includeHidden, ...rest } = params;
  return getJson<PageQueriesStats>('/pages/keywords', { ...rest, ...(includeHidden ? { includeHidden: '1' } : {}) }, signal);
}

export function readPageSyncStatus(signal?: AbortSignal): Promise<PageSyncStatus> {
  return getJson<PageSyncStatus>('/pages/sync/status', undefined, signal);
}

export function syncPagesNow(): Promise<unknown> {
  return sendJson<unknown>('post', '/pages/sync');
}

// ─── Analytics hide list ─────────────────────────────────────────────────────

// Only pages are hideable. Keyword rankings, sessions and every stat derive
// from the visible pages, so hiding a page drops its data everywhere.
export type ExclusionScope = 'page';

export interface ExclusionEntry {
  readonly value: string;
  readonly createdAt: string;
}

/** The hidden pages. */
export function listExclusions(scope: ExclusionScope, signal?: AbortSignal): Promise<{ scope: ExclusionScope; entries: ExclusionEntry[] }> {
  return getJson('/analytics/exclusions', { scope }, signal);
}

/** Hides a page (by path) from the analytics. */
export function hideItem(scope: ExclusionScope, value: string): Promise<unknown> {
  return sendJson('post', '/analytics/exclusions', { scope, value });
}

/** Unhides a page. */
export function unhideItem(scope: ExclusionScope, value: string): Promise<unknown> {
  return sendJson('delete', `/analytics/exclusions?scope=${scope}&value=${encodeURIComponent(value)}`);
}
